import * as React from "react";
import { NumericFormat } from "react-number-format";

import { cn } from "@/lib/utils";

type InputProps = Omit<React.ComponentProps<"input">, "prefix" | "suffix"> & {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  customPlaceholder?: React.ReactNode;
  maxLength?: number;
  allowZero?: boolean;
  thousandSeparator?: string;
};

function Input({
  className,
  type = "text",
  prefix,
  suffix,
  maxLength,
  customPlaceholder,
  allowZero = true,
  thousandSeparator = ",",
  ...props
}: InputProps) {
  return (
    <span
      data-disabled={props.disabled}
      className={cn(
        "file:text-foreground bg-background relative space-x-2 items-center selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border p-2.5 shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:font-medium",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        "data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-gray-100 data-[disabled=true]:border-gray-300",
        //"focus-within:border-ring focus-within:ring-[1px] focus-within:ring-ring/100",
        "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
        "h-[var(--control-height)]",
        className,
        props["value"],
      )}
      aria-invalid={props["aria-invalid"]}
    >
      {prefix && <span className="text-gray-500">{prefix}</span>}
      {type === "number" ? (
        <NumericFormat
          placeholder={props.placeholder}
          thousandSeparator={thousandSeparator}
          decimalScale={2}
          allowNegative={false}
          disabled={props.disabled}
          readOnly={props.disabled}
          data-has-value={!!props.value}
          data-slot="input"
          className={cn(
            "border-0 w-full peer disabled:cursor-not-allowed outline-0 placeholder:text-muted-foreground flex-1 text-start",
            props.disabled ? "pointer-events-none" : "",
          )}
          value={props.value as string | number | null | undefined}
          isAllowed={(values) => {
            const { value } = values;
            if (!allowZero && parseFloat(value) === 0) {
              return false;
            }
            return maxLength ? value.length <= maxLength : true;
          }}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo?.event) {
              const e = sourceInfo.event as React.ChangeEvent<HTMLInputElement>;
              if (e.target.value.length > 0) {
                e.target.setAttribute("data-has-value", "true");
              } else {
                e.target.setAttribute("data-has-value", "false");
              }

              e.target.value = values.value;

              props?.onChange?.(e);
            }
          }}
          onBlur={props.onBlur}
        />
      ) : (
        <input
          data-has-value={!!props.value}
          className="border-0 w-full peer disabled:cursor-not-allowed outline-0 placeholder:text-muted-foreground flex-1 text-start"
          type={type}
          data-slot="input"
          {...props}
          value={props.value === undefined ? undefined : props.value}
          onChange={(e) => {
            if (e.target.value.length > 0) {
              e.target.setAttribute("data-has-value", "true");
            } else {
              e.target.setAttribute("data-has-value", "false");
            }
            props?.onChange?.(e);
          }}
        />
      )}
      {suffix && <span className="text-gray-500 font-medium">{suffix}</span>}
      {customPlaceholder && (
        <span className="absolute peer-focus:hidden peer-data-[has-value=true]:hidden top-0 flex items-center pointer-events-none left-0 bottom-0 right-0">
          {customPlaceholder}
        </span>
      )}
    </span>
  );
}

export { Input };
